import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import useControlled from '@mui/utils/useControlled';
import React, { useCallback, useMemo, useState } from 'react';

import { QMenu } from '../QMenu/QMenu';
import { QMenuDivider, QMenuItem, QMenuSub, QMenuSubheader } from '../shared';
import type { QSelectOption, QSelectProps } from '../types';
import { QSelectButton } from './QSelectButton';

/** Resolve a leaf option label, walking nested `subMenu` trees. */
function findOptionLabel(options: QSelectOption[], value: string): string | undefined {
  for (const option of options) {
    if (option.subMenu && option.subMenu.length > 0) {
      const nested = findOptionLabel(option.subMenu, value);
      if (nested !== undefined) return nested;
      continue;
    }
    if (option.value === value) return option.label;
  }
  return undefined;
}

type RenderOptionsArgs = {
  options: QSelectOption[];
  multiple: boolean;
  value: string | string[];
  disabled: boolean;
  showCheckmark: boolean;
  onSingleSelect: (next: string) => void;
  onMultiToggle: (optionValue: string) => void;
};

function renderSelectOptions({
  options,
  multiple,
  value,
  disabled,
  showCheckmark,
  onSingleSelect,
  onMultiToggle,
}: RenderOptionsArgs): React.ReactNode[] {
  let lastGroup: string | undefined;
  let isFirst = true;
  const nodes: React.ReactNode[] = [];

  for (const option of options) {
    const subMenu = option.subMenu && option.subMenu.length > 0 ? option.subMenu : undefined;
    if (subMenu && multiple) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console -- unsupported combination; do not commit the parent value
        console.error(
          `QSelect: option "${String(option.label)}" (\`${option.value}\`) has subMenu, which is unsupported with multiple. Nested options are omitted and the parent is not selectable.`,
        );
      }
      continue;
    }

    const showGroupHeader = Boolean(option.group) && option.group !== lastGroup;
    const showGroupDivider = showGroupHeader && lastGroup !== undefined;
    lastGroup = option.group;
    const showDivider = showGroupDivider || (Boolean(option.dividerBefore) && !isFirst);
    isFirst = false;

    if (showDivider) {
      nodes.push(<QMenuDivider key={`${option.value}-divider`} />);
    }
    if (showGroupHeader && option.group) {
      nodes.push(<QMenuSubheader key={`${option.value}-header`} label={option.group} />);
    }

    if (subMenu) {
      const selectedChildLabel = typeof value === 'string' ? findOptionLabel(subMenu, value) : undefined;
      const selected = selectedChildLabel !== undefined;

      nodes.push(
        <QMenuSub
          key={option.value}
          id={option.id}
          label={option.label}
          subtext={selectedChildLabel}
          selected={selected}
          showCheckmark={showCheckmark}
          startIcon={option.startIcon}
          emptyStartIcon={option.emptyStartIcon}
          disabled={option.disabled || disabled}
        >
          {renderSelectOptions({
            options: subMenu,
            multiple,
            value,
            disabled,
            showCheckmark,
            onSingleSelect,
            onMultiToggle,
          })}
        </QMenuSub>,
      );
      continue;
    }

    const selected = multiple ? (value as string[]).includes(option.value) : (value as string) === option.value;

    nodes.push(
      <QMenuItem
        key={option.value}
        id={option.id}
        variant='option'
        label={option.label}
        subtext={option.subtext}
        selected={selected}
        multiple={multiple}
        showCheckmark={multiple ? undefined : showCheckmark}
        disabled={option.disabled || disabled}
        startIcon={option.startIcon}
        emptyStartIcon={option.emptyStartIcon}
        endIcon={option.endIcon}
        onClick={() => {
          if (multiple) {
            onMultiToggle(option.value);
          } else {
            onSingleSelect(option.value);
          }
        }}
      />,
    );
  }

  return nodes;
}

/**
 * Bordered pick-one / pick-many control composing QSelectButton + QMenu + QMenuItem option rows.
 * New DS primitive — planned replacement for SpendingPlanDropdown / NestedMenuSelect over time.
 */
export function QSelect(props: QSelectProps): React.ReactElement {
  const {
    options,
    disabled = false,
    id,
    placeholder = 'Select',
    renderValue,
    width,
    size,
    ButtonProps,
    showCheckmark = false,
    open: openProp,
    onOpenChange,
  } = props;

  // Callback-ref → state so a parent-forced `open` re-renders with a real anchorEl
  // (plain useRef.current is still null on the first open=true render).
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const setAnchorRef = useCallback((node: HTMLButtonElement | null) => {
    setAnchorEl((prev) => (prev === node ? prev : node));
  }, []);
  const [open, setOpen] = useControlled({
    controlled: openProp,
    default: false,
    name: 'QSelect',
  });

  const handleOpen = useCallback(() => {
    if (disabled) return;
    setOpen(true);
    onOpenChange?.(true);
  }, [disabled, setOpen, onOpenChange]);

  const handleClose = useCallback(() => {
    setOpen(false);
    onOpenChange?.(false);
  }, [setOpen, onOpenChange]);

  const triggerLabel = useMemo((): React.ReactNode => {
    if (renderValue) {
      return renderValue(props.value);
    }
    if (props.multiple) {
      if (props.value.length === 0) return placeholder;
      if (props.value.length === 1) {
        return findOptionLabel(options, props.value[0]) ?? placeholder;
      }
      return `${props.value.length} selected`;
    }
    if (!props.value) return placeholder;
    return findOptionLabel(options, props.value) ?? placeholder;
  }, [options, placeholder, props, renderValue]);

  const handleSingleSelect = useCallback(
    (next: string) => {
      if (!props.multiple) {
        props.onChange(next);
        handleClose();
      }
    },
    [handleClose, props],
  );

  const handleMultiToggle = useCallback(
    (optionValue: string) => {
      if (!props.multiple) return;
      const next = props.value.includes(optionValue)
        ? props.value.filter((v) => v !== optionValue)
        : [...props.value, optionValue];
      props.onChange(next);
    },
    [props],
  );

  const buttonId = id ? `${id}-button` : undefined;

  return (
    <>
      <QSelectButton
        {...ButtonProps}
        ref={setAnchorRef}
        id={buttonId}
        open={open}
        size={size}
        disabled={disabled}
        onClick={handleOpen}
        aria-controls={open ? id : undefined}
      >
        {triggerLabel}
      </QSelectButton>
      <QMenu
        id={id}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        width={width}
        variant='selectedMenu'
        // Bounded select panel + scroll arrows when the option list overflows.
        maxHeight={400}
        scrollArrows
        footer={
          props.multiple && props.showDoneButton ? (
            // Sticky paper chrome — not a MenuList item. "Done" only closes the menu;
            // selection already committed per-toggle via onChange (see showDoneButton
            // doc in types.ts). Not a staged/batched apply.
            <>
              <Divider sx={{ mx: -1, mb: 1 }} />
              <Button variant='contained' size='small' fullWidth onClick={handleClose}>
                Done
              </Button>
            </>
          ) : null
        }
      >
        {renderSelectOptions({
          options,
          multiple: Boolean(props.multiple),
          value: props.value,
          disabled,
          showCheckmark,
          onSingleSelect: handleSingleSelect,
          onMultiToggle: handleMultiToggle,
        })}
      </QMenu>
    </>
  );
}
